import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BookUser, Plus, Edit, Trash2, Search, Phone, Mail, Building2, MapPin, Star, Printer, X } from 'lucide-react';
import { addressBookAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const AddressBook = () => {
  const { canAdd, canEdit, canDelete, hasModuleAccess } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [filteredAddresses, setFilteredAddresses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [selectedAddresses, setSelectedAddresses] = useState([]);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [isDefaultAddressDialogOpen, setIsDefaultAddressDialogOpen] = useState(false);
  
  // Print settings
  const [printSettings, setPrintSettings] = useState({
    fontSize: 14,
    useBold: false,
  });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    gst: '',
    phones: [{ label: 'mobile', number: '' }],
    street: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'India',
    notes: '',
  });

  useEffect(() => {
    loadAddresses();
    loadDefaultAddress();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = addresses.filter(addr => 
        addr.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        addr.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        addr.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        addr.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        addr.gst?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        addr.phones?.some(p => p.number.includes(searchQuery))
      );
      setFilteredAddresses(filtered);
    } else {
      setFilteredAddresses(addresses);
    }
  }, [searchQuery, addresses]);

  const loadAddresses = async () => {
    setIsLoading(true);
    try {
      const data = await addressBookAPI.getAll();
      setAddresses(data);
      setFilteredAddresses(data);
    } catch (error) {
      toast.error('Failed to load addresses');
      console.error('Error loading addresses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDefaultAddress = async () => {
    try {
      const data = await addressBookAPI.getDefaultAddress();
      if (data.value) {
        setDefaultAddress(data.value);
      }
    } catch (error) {
      console.error('Error loading default address:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      company: '',
      gst: '',
      phones: [{ label: 'mobile', number: '' }],
      street: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'India',
      notes: '',
    });
  };

  const handleAddAddress = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    // Filter out empty phone numbers
    const phones = formData.phones.filter(p => p.number.trim() !== '');
    
    try {
      const newAddress = await addressBookAPI.create({ ...formData, phones });
      setAddresses([...addresses, newAddress]);
      setIsAddOpen(false);
      resetForm();
      toast.success('Address added successfully');
    } catch (error) {
      toast.error('Failed to add address');
      console.error('Error adding address:', error);
    }
  };

  const handleEditAddress = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    // Filter out empty phone numbers
    const phones = formData.phones.filter(p => p.number.trim() !== '');
    
    try {
      const updated = await addressBookAPI.update(editingAddress.id, { ...formData, phones });
      setAddresses(addresses.map(a => a.id === editingAddress.id ? updated : a));
      setIsEditOpen(false);
      setEditingAddress(null);
      resetForm();
      toast.success('Address updated successfully');
    } catch (error) {
      toast.error('Failed to update address');
      console.error('Error updating address:', error);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    try {
      await addressBookAPI.delete(id);
      setAddresses(addresses.filter(a => a.id !== id));
      toast.success('Address deleted successfully');
    } catch (error) {
      toast.error('Failed to delete address');
      console.error('Error deleting address:', error);
    }
  };

  const handleSetDefaultAddress = async (id) => {
    if (!window.confirm('Set this address as the default FROM address for printing labels?')) return;
    
    try {
      await addressBookAPI.setDefaultAddress(id);
      const address = addresses.find(a => a.id === id);
      setDefaultAddress(address);
      toast.success('Default FROM address set successfully');
    } catch (error) {
      toast.error('Failed to set default address');
      console.error('Error setting default address:', error);
    }
  };

  const openEditDialog = (address) => {
    setEditingAddress(address);
    setFormData({
      name: address.name || '',
      email: address.email || '',
      company: address.company || '',
      gst: address.gst || '',
      phones: address.phones && address.phones.length > 0 ? address.phones : [{ label: 'mobile', number: '' }],
      street: address.street || '',
      city: address.city || '',
      state: address.state || '',
      zip_code: address.zip_code || '',
      country: address.country || 'India',
      notes: address.notes || '',
    });
    setIsEditOpen(true);
  };

  const addPhoneField = () => {
    setFormData({
      ...formData,
      phones: [...formData.phones, { label: 'mobile', number: '' }]
    });
  };

  const removePhoneField = (index) => {
    const newPhones = formData.phones.filter((_, i) => i !== index);
    setFormData({ ...formData, phones: newPhones });
  };

  const updatePhone = (index, field, value) => {
    const newPhones = [...formData.phones];
    newPhones[index][field] = value;
    setFormData({ ...formData, phones: newPhones });
  };

  const toggleAddressSelection = (id) => {
    setSelectedAddresses(prev => 
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedAddresses.length === filteredAddresses.length) {
      setSelectedAddresses([]);
    } else {
      setSelectedAddresses(filteredAddresses.map(a => a.id));
    }
  };

  const openPrintDialog = () => {
    if (selectedAddresses.length === 0) {
      toast.error('Please select at least one address to print');
      return;
    }
    if (!defaultAddress) {
      toast.error('Please set a default FROM address first');
      return;
    }
    setIsPrintDialogOpen(true);
  };

  const handlePrint = () => {
    const selectedAddressList = addresses.filter(a => selectedAddresses.includes(a.id));
    const fontWeight = printSettings.useBold ? 'bold' : 'normal';
    
    const addressHTML = selectedAddressList.map(toAddr => `
      <div style="page-break-inside: avoid; margin-bottom: 40px; padding: 20px; font-size: ${printSettings.fontSize}px; font-weight: ${fontWeight}; line-height: 1.3;">
        <div style="margin-bottom: 8px;">
          <div style="font-size: ${printSettings.fontSize + 2}px; margin-bottom: 3px;">TO:</div>
          <div>${toAddr.name}</div>
          ${toAddr.company ? `<div>${toAddr.company}</div>` : ''}
          ${toAddr.gst ? `<div>GST: ${toAddr.gst}</div>` : ''}
          ${toAddr.street ? `<div>${toAddr.street}</div>` : ''}
          ${toAddr.city || toAddr.state || toAddr.zip_code ? `<div>${[toAddr.city, toAddr.state, toAddr.zip_code].filter(Boolean).join(' ')}</div>` : ''}
          ${toAddr.country ? `<div>${toAddr.country}</div>` : ''}
          ${toAddr.phones && toAddr.phones.length > 0 ? `<div>${toAddr.phones[0].number}</div>` : ''}
        </div>
        <div style="border-top: 1px solid #ccc; padding-top: 5px;">
          <div style="font-size: ${printSettings.fontSize + 2}px; margin-bottom: 3px;">FROM:</div>
          <div>${defaultAddress.name}</div>
          ${defaultAddress.company ? `<div>${defaultAddress.company}</div>` : ''}
          ${defaultAddress.gst ? `<div>GST: ${defaultAddress.gst}</div>` : ''}
          ${defaultAddress.street ? `<div>${defaultAddress.street}</div>` : ''}
          ${defaultAddress.city || defaultAddress.state || defaultAddress.zip_code ? `<div>${[defaultAddress.city, defaultAddress.state, defaultAddress.zip_code].filter(Boolean).join(' ')}</div>` : ''}
          ${defaultAddress.country ? `<div>${defaultAddress.country}</div>` : ''}
          ${defaultAddress.phones && defaultAddress.phones.length > 0 ? `<div>${defaultAddress.phones[0].number}</div>` : ''}
        </div>
      </div>
    `).join('');
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Address Labels - ${new Date().toLocaleDateString()}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              padding: 10px; 
              margin: 0;
            }
            @media print { 
              body { padding: 5px; }
              @page { margin: 10mm; }
            }
          </style>
        </head>
        <body>
          ${addressHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    setIsPrintDialogOpen(false);
  };

  const openDefaultAddressDialog = () => {
    if (defaultAddress) {
      setFormData({
        name: defaultAddress.name || '',
        email: defaultAddress.email || '',
        company: defaultAddress.company || '',
        gst: defaultAddress.gst || '',
        phones: defaultAddress.phones && defaultAddress.phones.length > 0 ? defaultAddress.phones : [{ label: 'mobile', number: '' }],
        street: defaultAddress.street || '',
        city: defaultAddress.city || '',
        state: defaultAddress.state || '',
        zip_code: defaultAddress.zip_code || '',
        country: defaultAddress.country || 'India',
        notes: defaultAddress.notes || '',
      });
    } else {
      resetForm();
    }
    setIsDefaultAddressDialogOpen(true);
  };

  const handleSaveDefaultAddress = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    const phones = formData.phones.filter(p => p.number.trim() !== '');
    
    try {
      // Create or update the address
      let savedAddress;
      if (defaultAddress && defaultAddress.id) {
        // Update existing default address
        savedAddress = await addressBookAPI.update(defaultAddress.id, { ...formData, phones });
        setAddresses(addresses.map(a => a.id === defaultAddress.id ? savedAddress : a));
      } else {
        // Create new address
        savedAddress = await addressBookAPI.create({ ...formData, phones });
        setAddresses([...addresses, savedAddress]);
      }
      
      // Set as default
      await addressBookAPI.setDefaultAddress(savedAddress.id);
      setDefaultAddress(savedAddress);
      setIsDefaultAddressDialogOpen(false);
      resetForm();
      toast.success('Default FROM address saved successfully');
    } catch (error) {
      toast.error('Failed to save default address');
      console.error('Error saving default address:', error);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-primary/10">
            <BookUser className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Address Book</h1>
            <p className="text-muted-foreground mt-1">Manage contacts and addresses</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openDefaultAddressDialog}>
            <Star className="h-4 w-4 mr-2" />
            {defaultAddress ? 'Edit' : 'Set'} Default FROM
          </Button>
          <Button 
            variant="outline" 
            onClick={openPrintDialog}
            disabled={selectedAddresses.length === 0}
          >
            <Printer className="h-4 w-4 mr-2" />
            Print Labels ({selectedAddresses.length})
          </Button>
          <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
            {canAdd('address-book') && (
              <DialogTrigger asChild>
                <Button className="shadow-emerald" onClick={resetForm}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contact
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Contact</DialogTitle>
                <DialogDescription>Add a new address to your address book</DialogDescription>
              </DialogHeader>
              <AddressFormContent
                formData={formData}
                setFormData={setFormData}
                updatePhone={updatePhone}
                addPhoneField={addPhoneField}
                removePhoneField={removePhoneField}
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button>
                <Button onClick={handleAddAddress}>Add Contact</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Default Address Card */}
      {defaultAddress && (
        <Card className="border-primary/50 bg-primary/5">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 text-primary fill-primary" />
                <CardTitle className="text-lg">Default FROM Address</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="font-semibold text-lg">{defaultAddress.name}</p>
                {defaultAddress.company && <p className="text-sm text-muted-foreground">{defaultAddress.company}</p>}
                {defaultAddress.gst && <p className="text-sm">GST: {defaultAddress.gst}</p>}
              </div>
              <div className="text-sm">
                {defaultAddress.street && <p>{defaultAddress.street}</p>}
                {(defaultAddress.city || defaultAddress.state) && (
                  <p>{defaultAddress.city} {defaultAddress.state} {defaultAddress.zip_code}</p>
                )}
                {defaultAddress.country && <p>{defaultAddress.country}</p>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search by name, email, company, GST, city, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Address List */}
      <Card>
        <CardHeader>
          <CardTitle>Contacts ({filteredAddresses.length})</CardTitle>
          <CardDescription>All your business contacts and addresses</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading addresses...</p>
            </div>
          ) : filteredAddresses.length === 0 ? (
            <div className="text-center py-12">
              <BookUser className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">
                {searchQuery ? 'No addresses found' : 'No contacts yet'}
              </p>
              <p className="text-muted-foreground">
                {searchQuery ? 'Try a different search term' : 'Click "Add Contact" to create your first contact'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedAddresses.length === filteredAddresses.length && filteredAddresses.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Company / GST</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAddresses.map((address) => (
                    <TableRow key={address.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedAddresses.includes(address.id)}
                          onChange={() => toggleAddressSelection(address.id)}
                          className="rounded border-gray-300"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {defaultAddress?.id === address.id && (
                            <Star className="h-4 w-4 text-primary fill-primary" />
                          )}
                          <div>
                            <p className="font-medium">{address.name}</p>
                            {address.notes && (
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {address.notes}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {address.phones && address.phones.length > 0 && (
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="h-3 w-3" />
                              <span>{address.phones[0].number}</span>
                              {address.phones.length > 1 && (
                                <Badge variant="secondary" className="text-xs">+{address.phones.length - 1}</Badge>
                              )}
                            </div>
                          )}
                          {address.email && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              <span>{address.email}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          {address.company && (
                            <div className="flex items-center gap-1 text-sm">
                              <Building2 className="h-3 w-3" />
                              <span>{address.company}</span>
                            </div>
                          )}
                          {address.gst && (
                            <p className="text-xs text-muted-foreground">GST: {address.gst}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(address.city || address.state) && (
                          <div className="flex items-center gap-1 text-sm">
                            <MapPin className="h-3 w-3" />
                            <span>{address.city}{address.state ? `, ${address.state}` : ''}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {defaultAddress?.id !== address.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSetDefaultAddress(address.id)}
                              title="Set as default FROM address"
                            >
                              <Star className="h-4 w-4" />
                            </Button>
                          )}
                          {canEdit('address-book') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(address)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete('address-book') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteAddress(address.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>Update contact information</DialogDescription>
          </DialogHeader>
          <AddressFormContent
            formData={formData}
            setFormData={setFormData}
            updatePhone={updatePhone}
            addPhoneField={addPhoneField}
            removePhoneField={removePhoneField}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEditAddress}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Settings Dialog */}
      <Dialog open={isPrintDialogOpen} onOpenChange={setIsPrintDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Print Address Labels</DialogTitle>
            <DialogDescription>
              Printing {selectedAddresses.length} label(s) with default FROM address
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="fontSize">Font Size: {printSettings.fontSize}px</Label>
              <input
                id="fontSize"
                type="range"
                min="10"
                max="24"
                value={printSettings.fontSize}
                onChange={(e) => setPrintSettings({ ...printSettings, fontSize: parseInt(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>10px</span>
                <span>24px</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                id="useBold"
                type="checkbox"
                checked={printSettings.useBold}
                onChange={(e) => setPrintSettings({ ...printSettings, useBold: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="useBold" className="cursor-pointer">Use Bold Text</Label>
            </div>
            {!defaultAddress && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  ⚠️ No default FROM address set. Please set one before printing.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPrintDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePrint} disabled={!defaultAddress}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Default FROM Address Dialog */}
      <Dialog open={isDefaultAddressDialogOpen} onOpenChange={setIsDefaultAddressDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{defaultAddress ? 'Edit' : 'Set'} Default FROM Address</DialogTitle>
            <DialogDescription>
              This address will be used as the FROM address in all printed labels
            </DialogDescription>
          </DialogHeader>
          <AddressFormContent
            formData={formData}
            setFormData={setFormData}
            updatePhone={updatePhone}
            addPhoneField={addPhoneField}
            removePhoneField={removePhoneField}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDefaultAddressDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveDefaultAddress}>
              <Star className="h-4 w-4 mr-2" />
              Save as Default
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Form Content Component (reusable for Add/Edit)
const AddressFormContent = ({ formData, setFormData, updatePhone, addPhoneField, removePhoneField }) => {
  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Full name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="email@example.com"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="company">Company</Label>
          <Input
            id="company"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            placeholder="Company name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="gst">GST Number</Label>
          <Input
            id="gst"
            value={formData.gst}
            onChange={(e) => setFormData({ ...formData, gst: e.target.value })}
            placeholder="29ABCDE1234F1Z5"
          />
        </div>
      </div>

      {/* Phone Numbers */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Phone Numbers</Label>
          <Button type="button" size="sm" variant="outline" onClick={addPhoneField}>
            <Plus className="h-3 w-3 mr-1" />
            Add Phone
          </Button>
        </div>
        {formData.phones.map((phone, index) => (
          <div key={index} className="flex gap-2">
            <select
              value={phone.label}
              onChange={(e) => updatePhone(index, 'label', e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="mobile">Mobile</option>
              <option value="work">Work</option>
              <option value="home">Home</option>
              <option value="fax">Fax</option>
            </select>
            <Input
              value={phone.number}
              onChange={(e) => updatePhone(index, 'number', e.target.value)}
              placeholder="Phone number"
              className="flex-1"
            />
            {formData.phones.length > 1 && (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => removePhoneField(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <Label htmlFor="street">Street Address</Label>
        <Input
          id="street"
          value={formData.street}
          onChange={(e) => setFormData({ ...formData, street: e.target.value })}
          placeholder="Street address"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city">City</Label>
          <Input
            id="city"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            placeholder="City"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="state">State</Label>
          <Input
            id="state"
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            placeholder="State"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="zip_code">ZIP Code</Label>
          <Input
            id="zip_code"
            value={formData.zip_code}
            onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
            placeholder="ZIP"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="country">Country</Label>
        <Input
          id="country"
          value={formData.country}
          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
          placeholder="Country"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Input
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Additional notes"
        />
      </div>
    </div>
  );
};

export default AddressBook;
